import type { ReactNode } from 'react';
import { Dialog } from './Dialog';
import { Button } from './Button';
import './AlertDialog.css';

export interface AlertDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** DESIGN.md §7: "Destructive confirmations name the object and the count" — callers pass the
   * exact sentence (e.g. `Hide 12 songs from the library?`) rather than a generic template. */
  title: string;
  description?: ReactNode;
  confirmLabel: string;
  /** MVP-331: was optional with an English `'Cancel'` default, which is copy — it would have
   * rendered English to a Thai operator the first time a caller left it off. All five call sites
   * already pass `t('common.cancel')`; required is the version of that default the build checks.
   * `ui/` stays dictionary-free (see `ui/SearchField.tsx`'s rule) — so the fix is to demand the
   * string, not to resolve one here. */
  cancelLabel: string;
  /** The header close button's accessible name, handed down to `Dialog`. Required for the same
   * reason `cancelLabel` is — `ui/AlertDialog.tsx` was the one `Dialog` caller passing nothing, so
   * its close button fell through to `Dialog`'s own English default and MVP-289's check caught it
   * the moment this file was enrolled. */
  closeLabel: string;
  /** `danger` for destructive/hard-to-reverse actions (DESIGN.md §2.4 — never the same visual
   * weight as a neutral action); `primary` for a confirmation that is not itself destructive. */
  tone?: 'danger' | 'primary';
  confirmLoading?: boolean;
}

/** DESIGN.md §9.1 `AlertDialog` (typed confirmation) — built on top of `Dialog` rather than a
 * second portal/focus-trap implementation. "Typed" here means "states exactly what will happen",
 * not literally requiring the operator to retype a name — every caller so far confirms a bounded,
 * recoverable-by-a-human action, not an irreversible delete. */
export function AlertDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  cancelLabel,
  closeLabel,
  tone = 'danger',
  confirmLoading = false,
}: AlertDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} title={title} closeLabel={closeLabel}>
      <div className="alert-dialog">
        {description && <p className="alert-dialog__description text-body">{description}</p>}
        <div className="alert-dialog__actions">
          <Button variant="secondary" onClick={onClose} disabled={confirmLoading}>
            {cancelLabel}
          </Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} onClick={onConfirm} loading={confirmLoading}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
